import { Alert, Card, Container, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
    SaveButton,
    SimpleForm,
    Toolbar,
    useDataProvider,
    useEditController,
    useNotify,
    useRecordContext,
    useRedirect,
} from 'react-admin';
import { SubmitHandler } from 'react-hook-form';
import { IrmDataProvider } from '../../providers/types';
import { User, UpdateUserPatch } from '../../types/types';
import { UsersInputs } from './UserInputs';
import { DeleteUserButton } from '../admin/DeleteUserButton';
import { UserApiKeys } from '../admin/UserApiKeys';

function EditToolbar({
    uid,
    email,
    role,
}: {
    uid: string;
    email: string;
    role?: string;
}) {
    return (
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <SaveButton />
            <DeleteUserButton uid={uid} email={email} role={role} />
        </Toolbar>
    );
}

export function UsersEdit() {
    const { record } = useEditController({ resource: 'users' });
    const dataProvider = useDataProvider<IrmDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();

    const { mutate } = useMutation({
        mutationKey: ['admin-update-user'],
        mutationFn: async (patch: UpdateUserPatch) => {
            if (!record?.id) throw new Error('Record not found');
            return dataProvider.adminUpdateUser(record.id, patch);
        },
        onSuccess: () => {
            notify('User updated.');
            redirect('/admin/users');
        },
        onError: (e: any) =>
            notify(e?.message || 'Update failed.', { type: 'error' }),
    });

    const onSubmit: SubmitHandler<any> = data => {
        const patch: UpdateUserPatch = {
            role: data.role,
            allowed_features: data.allowed_features ?? [],
            status: data.status,
            first_name: data.first_name,
            last_name: data.last_name,
            gender: data.gender,
            company: data.company,
        };
        mutate(patch);
    };

    if (!record) return null;

    const keyHolder = {
        id: String(record.id),
        email: record.email as string | undefined,
        role: record.role as string | undefined,
        status: record.status as string | undefined,
        allowed_features: record.allowed_features as string[] | undefined,
    };

    if (record.role === 'admin') {
        // Every admin's keys are managed here by any admin: an admin has to be
        // able to cut off, or issue, another admin's key. The server never
        // restricted this (no target-role check); only this page did. The rest
        // of an admin record stays console-only.
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Card sx={{ p: 3 }}>
                    <Stack gap={2}>
                        <Typography variant="h6">
                            {record.first_name} {record.last_name}
                        </Typography>
                        <Alert severity="info">
                            Admin users are managed via the Firestore console
                            and cannot be edited or deleted here.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Email: {String(record.email)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Status: {String(record.status)}
                        </Typography>
                    </Stack>
                </Card>
                <UserApiKeys user={keyHolder} managedInConsole />
            </Container>
        );
    }

    return (
        // md rather than sm: the page carries the API keys table as well as the
        // profile form now, and six columns do not fit in sm.
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Card>
                <SimpleForm
                    toolbar={
                        <EditToolbar
                            uid={String(record.id)}
                            email={String(record.email)}
                            role={record.role as string | undefined}
                        />
                    }
                    onSubmit={onSubmit}
                    record={record}
                >
                    <UserEditTitle />
                    <UsersInputs />
                </SimpleForm>
            </Card>
            {/* Keys belong to a user, so they are managed here rather than on a
                separate page — next to the `api` feature checkbox above, which
                is what decides whether this user may hold one at all. */}
            <UserApiKeys user={keyHolder} />
        </Container>
    );
}

const UserEditTitle = () => {
    const record = useRecordContext<User>();
    if (!record) return null;
    return (
        <Typography variant="h6">
            Edit {record.first_name} {record.last_name}
        </Typography>
    );
};
